/**
 * Central SEO configuration for Surflife.
 * Site languages: nl, en, de (French is search-intent only, not a site language).
 */

export const SITE_ORIGIN = "https://www.surflife.eu";
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/surflife.png`;
export const SITE_NAME = "Surflife";

/** Canonical indexable routes grouped by language alternates */
export const ROUTE_ALTERNATES = [
  {
    id: "home",
    nl: "/nl",
    en: "/en",
    de: "/de",
    priority: "1.0",
    changefreq: "weekly",
  },
  {
    id: "terms",
    nl: "/voorwaarden",
    en: "/termsAndCondition",
    de: "/geschaftsbedingungen",
    priority: "0.5",
    changefreq: "yearly",
  },
  {
    id: "privacy",
    nl: "/privacybeleid",
    en: "/privacyPolicy",
    de: "/datenschutz",
    priority: "0.5",
    changefreq: "yearly",
  },
  {
    id: "insurance",
    nl: "/verzekering",
    en: "/insurance",
    de: "/Versicherung",
    priority: "0.7",
    changefreq: "monthly",
  },
  {
    id: "information",
    nl: "/informatie",
    en: "/information",
    de: "/informationen",
    priority: "0.8",
    changefreq: "monthly",
  },
  {
    id: "groups",
    nl: "/groepsreizen",
    en: "/groups",
    de: "/gruppe",
    priority: "0.8",
    changefreq: "monthly",
  },
  {
    id: "crew",
    nl: "/crew-nl",
    en: "/crew",
    de: "/crew-de",
    priority: "0.7",
    changefreq: "monthly",
  },
];

/** Paths that duplicate canonical routes — should redirect, not be indexed */
export const DUPLICATE_REDIRECTS = [
  { source: "/home", destination: "/en", permanent: true },
  { source: "/surf-camps", destination: "/en", permanent: true },
  { source: "/surfcamp-mimizan", destination: "/en", permanent: true },
  { source: "/surflife-founders", destination: "/crew", permanent: true },
  { source: "/group-travel", destination: "/groups", permanent: true },
  { source: "/terms-conditions", destination: "/termsAndCondition", permanent: true },
  { source: "/contact", destination: "/information", permanent: true },
  { source: "/geschäftsbedingungen", destination: "/geschaftsbedingungen", permanent: true },
  { source: "/versicherung", destination: "/Versicherung", permanent: true },
];

export const PAGE_SEO = {
  "/nl": {
    lang: "nl",
    title: "Surfcamp in Les Landes, Frankrijk | Surflife Bias-Mimizan",
    description:
      "Surflife is een surfkamp in Les Landes, Frankrijk, rond Bias en Mimizan. Surfles, accommodatie en surfvakanties aan de Atlantische kust.",
    h1Fallback: "Surfcamp in Les Landes, Frankrijk",
  },
  "/en": {
    lang: "en",
    title: "Surf Camp in Les Landes, France | Surflife Bias-Mimizan",
    description:
      "Surflife is a surf camp in Les Landes, France, near Bias and Mimizan. Surf lessons, accommodation and surf holidays on the Atlantic coast.",
    h1Fallback: "Surf Camp in Les Landes, France",
  },
  "/de": {
    lang: "de",
    title: "Surf Camp in Les Landes, Frankreich | Surflife Bias-Mimizan",
    description:
      "Surflife ist ein Surfcamp in Les Landes, Frankreich, bei Bias und Mimizan. Surfunterricht, Unterkunft und Surfurlaub an der Atlantikküste.",
    h1Fallback: "Surf Camp in Les Landes, Frankreich",
  },
  "/voorwaarden": {
    lang: "nl",
    title: "Algemene Voorwaarden | Surflife",
    description:
      "Algemene voorwaarden van Surflife voor surfcamps en surfvakanties in Bias-Mimizan, Les Landes, Frankrijk.",
  },
  "/privacybeleid": {
    lang: "nl",
    title: "Privacybeleid | Surflife",
    description:
      "Privacybeleid van Surflife over hoe we persoonsgegevens verwerken voor onze surfcamps in Frankrijk.",
  },
  "/verzekering": {
    lang: "nl",
    title: "Verzekering Surfcamp Frankrijk | Surflife",
    description:
      "Informatie over verzekering tijdens je Surflife surfcamp in Bias-Mimizan, Les Landes.",
  },
  "/informatie": {
    lang: "nl",
    title: "Praktische Informatie Surfcamp | Surflife Les Landes",
    description:
      "Praktische informatie over Surflife: locatie rond Bias en Mimizan, bereikbaarheid en wat je kunt verwachten.",
  },
  "/groepsreizen": {
    lang: "nl",
    title: "Groepsreizen Surfcamp Frankrijk | Surflife",
    description:
      "Plan een groepsreis of schoolreis naar Surflife in Bias-Mimizan, Les Landes. Surfen en accommodatie voor groepen.",
  },
  "/crew-nl": {
    lang: "nl",
    title: "Crew & Team | Surflife Surfcamp",
    description:
      "Maak kennis met de Surflife crew in Bias-Mimizan, Les Landes, Frankrijk.",
  },
  "/termsAndCondition": {
    lang: "en",
    title: "Terms and Conditions | Surflife",
    description:
      "Terms and conditions for Surflife surf camps and surf holidays in Bias-Mimizan, Les Landes, France.",
  },
  "/privacyPolicy": {
    lang: "en",
    title: "Privacy Policy | Surflife",
    description:
      "Surflife privacy policy explaining how we handle personal data for our surf camps in France.",
  },
  "/insurance": {
    lang: "en",
    title: "Surf Camp Insurance | Surflife France",
    description:
      "Insurance information for your Surflife surf camp stay near Bias and Mimizan in Les Landes.",
  },
  "/information": {
    lang: "en",
    title: "Practical Information | Surflife Surf Camp Les Landes",
    description:
      "Practical information about Surflife: location near Bias and Mimizan, getting there, and what to expect.",
  },
  "/groups": {
    lang: "en",
    title: "Group Surf Holidays France | Surflife Les Landes",
    description:
      "Plan a group or school trip to Surflife near Bias and Mimizan. Surfing and accommodation for groups.",
  },
  "/crew": {
    lang: "en",
    title: "Crew & Team | Surflife Surf Camp",
    description:
      "Meet the Surflife crew at our surf camp near Bias and Mimizan in Les Landes, France.",
  },
  "/geschaftsbedingungen": {
    lang: "de",
    title: "Geschäftsbedingungen | Surflife",
    description:
      "Geschäftsbedingungen von Surflife für Surfcamps und Surfurlaube in Bias-Mimizan, Les Landes, Frankreich.",
  },
  "/datenschutz": {
    lang: "de",
    title: "Datenschutz | Surflife",
    description:
      "Datenschutzrichtlinie von Surflife zur Verarbeitung personenbezogener Daten für unsere Surfcamps in Frankreich.",
  },
  "/Versicherung": {
    lang: "de",
    title: "Versicherung Surfcamp Frankreich | Surflife",
    description:
      "Informationen zur Versicherung während Ihres Surflife-Surfcamps in Bias-Mimizan, Les Landes.",
  },
  "/informationen": {
    lang: "de",
    title: "Praktische Informationen | Surflife Surfcamp Les Landes",
    description:
      "Praktische Informationen zu Surflife: Lage bei Bias und Mimizan, Anreise und was Sie erwartet.",
  },
  "/gruppe": {
    lang: "de",
    title: "Gruppenreisen Surfcamp Frankreich | Surflife",
    description:
      "Planen Sie eine Gruppen- oder Klassenfahrt zu Surflife bei Bias und Mimizan. Surfen und Unterkunft für Gruppen.",
  },
  "/crew-de": {
    lang: "de",
    title: "Crew & Team | Surflife Surfcamp",
    description:
      "Lernen Sie die Surflife-Crew in unserem Surfcamp bei Bias und Mimizan in den Landes kennen.",
  },
};

export function normalizePath(pathname) {
  if (!pathname || pathname === "/") return "/";
  return pathname.replace(/\/$/, "") || "/";
}

export function findAlternateGroup(pathname) {
  const path = normalizePath(pathname);
  if (path === "/") {
    return ROUTE_ALTERNATES.find((group) => group.id === "home");
  }
  return ROUTE_ALTERNATES.find(
    (group) => group.nl === path || group.en === path || group.de === path
  );
}

export function getPageSeo(pathname) {
  const path = normalizePath(pathname);
  const seoPath = path === "/" ? "/nl" : path;
  if (PAGE_SEO[seoPath]) return { path: seoPath, ...PAGE_SEO[seoPath] };

  // Fallback by language segment
  if (path.startsWith("/de") || path.includes("de")) {
    return { path, ...PAGE_SEO["/de"], title: `${SITE_NAME} | Surf Camp` };
  }
  if (path.startsWith("/en")) {
    return { path, ...PAGE_SEO["/en"], title: `${SITE_NAME} | Surf Camp` };
  }
  return { path, ...PAGE_SEO["/nl"], title: `${SITE_NAME} | Surfkamp` };
}

export function buildHreflangLinks(pathname) {
  const group = findAlternateGroup(pathname);
  if (!group) {
    return [
      { hreflang: "x-default", href: `${SITE_ORIGIN}/nl` },
    ];
  }
  return [
    { hreflang: "nl", href: `${SITE_ORIGIN}${group.nl}` },
    { hreflang: "en", href: `${SITE_ORIGIN}${group.en}` },
    { hreflang: "de", href: `${SITE_ORIGIN}${group.de}` },
    { hreflang: "x-default", href: `${SITE_ORIGIN}${group.nl}` },
  ];
}

const LOCAL_BUSINESS_DESCRIPTIONS = {
  nl: "Surfkamp en surfvakanties in Les Landes, Frankrijk, rond Bias en Mimizan aan de Atlantische kust.",
  en: "Surf camp and surf holidays in Les Landes, France, near Bias and Mimizan on the Atlantic coast.",
  de: "Surfcamp und Surfurlaube in Les Landes, Frankreich, bei Bias und Mimizan an der Atlantikküste.",
};

export function buildLocalBusinessJsonLd(lang = "en") {
  return {
    "@context": "https://schema.org",
    "@type": ["SportsActivityLocation", "LodgingBusiness", "LocalBusiness"],
    "@id": `${SITE_ORIGIN}/#organization`,
    name: "Surflife",
    url: SITE_ORIGIN,
    image: DEFAULT_OG_IMAGE,
    logo: `${SITE_ORIGIN}/full_logo512.png`,
    description: LOCAL_BUSINESS_DESCRIPTIONS[lang] || LOCAL_BUSINESS_DESCRIPTIONS.en,
    address: {
      "@type": "PostalAddress",
      addressLocality: "Bias",
      addressRegion: "Les Landes",
      addressCountry: "FR",
    },
    areaServed: [
      { "@type": "Place", name: "Bias" },
      { "@type": "Place", name: "Mimizan" },
      { "@type": "Place", name: "Les Landes" },
      { "@type": "Country", name: "France" },
    ],
    sameAs: [
      "https://www.facebook.com/Surflife1991/",
      "https://www.instagram.com/surflifetrips/",
      "https://www.youtube.com/@SURFLIFENL",
      "https://www.linkedin.com/company/surflife-nl-b-v-/",
    ],
  };
}

/** Build FAQPage JSON-LD from visible Q&A pairs (plain-text answers only). */
export function buildFaqPageJsonLd(faqs = []) {
  const entities = faqs
    .filter((f) => f && f.question && f.answer)
    .map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.answer,
      },
    }));
  if (!entities.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: entities,
  };
}

export function buildWebPageJsonLd({ title, description, path, lang }) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    description,
    url: `${SITE_ORIGIN}${path}`,
    inLanguage: lang,
    isPartOf: {
      "@type": "WebSite",
      name: SITE_NAME,
      url: SITE_ORIGIN,
    },
    about: { "@id": `${SITE_ORIGIN}/#organization` },
  };
}

export function buildBreadcrumbJsonLd(pathname) {
  const path = normalizePath(pathname);
  const seo = getPageSeo(path);
  const items = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Surflife",
      item: `${SITE_ORIGIN}/${seo.lang}`,
    },
  ];
  if (!["/nl", "/en", "/de"].includes(path)) {
    items.push({
      "@type": "ListItem",
      position: 2,
      name: seo.title.split("|")[0].trim(),
      item: `${SITE_ORIGIN}${path}`,
    });
  }
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
}

/** All paths to prerender at build time */
export function getPrerenderPaths() {
  const paths = new Set(["/", "/nl", "/en", "/de"]);
  ROUTE_ALTERNATES.forEach((group) => {
    paths.add(group.nl);
    paths.add(group.en);
    paths.add(group.de);
  });
  return Array.from(paths);
}
